import { Component, inject, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SharedModule } from '@shared/shared.module';
import appPackage from 'package.json';
import { LocalizationService } from 'src/app/internationalization/localization.service';
import { NewPasswordFormComponent } from './component/new-password-form/new-password-form.component';
import { MessageBoxComponent } from './component/message-box/message-box.component';
import { PasswordResetService, PasswordResetSessionPayload } from './service/password-reset.service';

@UntilDestroy()
@Component({
  selector: 'app-password-reset',
  templateUrl: './password-reset.component.html',
  styleUrls: ['./password-reset.component.scss'],
  imports: [SharedModule, NewPasswordFormComponent, MessageBoxComponent],
})
export class PasswordResetComponent implements OnInit {
  private translateService = inject(LocalizationService);
  readonly title;
  appName = appPackage.title;
  appVersion = `v${appPackage.version}`;
  isValidSessionQuery = false;
  isValidToken = false;
  isSubmitSuccess = false;

  constructor(
    private readonly titleService: Title,
    private readonly router: Router,
    private route: ActivatedRoute,
    private readonly passwordResetService: PasswordResetService,
  ) {
    this.title = this.translateService.translate('passwordReset.passwordReset');
  }

  /**
   * Check token validity and unsubmit to render new password form.
   */
  get isShowNewPasswordForm() {
    return this.isValidToken && !this.isSubmitSuccess;
  }

  /**
   * Check to render message box.
   */
  get isShowMessageBox() {
    return !this.isShowNewPasswordForm;
  }

  ngOnInit(): void {
    this.titleService.setTitle(this.title);
    this.extractSessionTokenFromQuery();
    this.validateSessionToken();
  }

  /**
   * Listen new password form submit response event.
   * @param {boolean} event - Response status.
   */
  onSubmitSuccess(event: boolean) {
    this.isSubmitSuccess = event;
    if (event) this.clearSession();
  }

  /**
   * Call API to validate password reset session.
   */
  private validateSessionToken() {
    if (!this.isValidSessionQuery) return;

    this.passwordResetService
      .validateSessionToken()
      .pipe(untilDestroyed(this))
      .subscribe((res) => {
        this.isValidToken = res.isValid;
      });
  }

  /**
   * Get password reset session from query string.
   */
  private extractSessionTokenFromQuery() {
    const queryString = this.router.url?.split('?')[1];

    if (!queryString) return this.setSessionValidStatus(false);

    const decodedQueryString = decodeURIComponent(queryString);
    const queryObj = this.splitQueryStringToObject(decodedQueryString);
    const base64QueryParam = queryObj['query'];

    if (!base64QueryParam) return this.setSessionValidStatus(false);

    const queryParamsString = atob(base64QueryParam);
    const queryParamsObj = this.splitQueryStringToObject<PasswordResetSessionPayload>(queryParamsString);

    const isValidSession = !!queryParamsObj['token'] && !!queryParamsObj['email'];
    this.setSessionValidStatus(isValidSession);
    isValidSession && this.passwordResetService.set('sessionPayload', queryParamsObj);
  }

  private setSessionValidStatus(status: boolean) {
    this.isValidSessionQuery = status;
  }

  private splitQueryStringToObject<T = Record<string, string>>(text: string) {
    let obj = {} as T;

    const searchParams = new URLSearchParams(text);
    for (const [key, value] of searchParams.entries()) {
      obj = { ...obj, [key]: value };
    }

    return obj;
  }

  /**
   * Clear session data in query string & service state.
   */
  private clearSession() {
    this.passwordResetService.set('sessionPayload', {} as PasswordResetSessionPayload);
    this.router.navigate([], { relativeTo: this.route, queryParams: {} });
  }
}
